import { useCallback, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useStytchUser } from "@louise/api/useStytch";
import { useApiClient } from "@louise/api/louise-api";
import { ApiRequestError, throwApiError } from "@src/packages/api/api-error";
import { retryLouiseQuery } from "@src/packages/api/retry-policy";
import {
  createChatwootConversation,
  findChatwootConversation,
  identifyChatwootContact,
  listChatwootMessages,
  sendChatwootMessage,
  type ChatwootContactSession,
  type SupportAttachmentDraft,
  type SupportConversation,
  type SupportMessage,
} from "../chatwootClient";
import { subscribeToChatwootRoom } from "../chatwootRealtime";

// Re-export the modal-facing types so callers keep importing them from the hook
// while the Chatwoot wire types live next to the client.
export type {
  SupportAttachment,
  SupportAttachmentDraft,
  SupportConversation,
  SupportMessage,
} from "../chatwootClient";

type EntryPoint = "support" | "feedback";

type SupportSession = ChatwootContactSession & {
  conversationId: number | null;
};

function sessionQueryKey(stytchUserId: string | null) {
  // Keep support threads isolated in the local query cache. Without this,
  // logging out and back in as a different person could briefly surface the
  // previous person's messages while the next request is in flight.
  return ["support-chatwoot-session", stytchUserId ?? "unknown"] as const;
}

function messagesQueryKey(
  stytchUserId: string | null,
  conversationId: number | null,
) {
  return [
    "support-chatwoot-messages",
    stytchUserId ?? "unknown",
    conversationId ?? "pending",
  ] as const;
}

/**
 * Louise-owned support UX, powered by Chatwoot's Client API (Option B for
 * #3357). Identity is minted by our own backend so Chatwoot links every session
 * to one stable contact; the bespoke modal keeps rendering the thread.
 *
 * Flow: `GET /me/support/identity` (typed openapi-fetch client) →
 * create/identify Chatwoot contact → reuse/create conversation → list/post
 * messages. Identifiers and the pubsub token live only in the React Query cache
 * for the session — never persisted to AsyncStorage/localStorage — and are
 * re-minted from the endpoint each session.
 */
export function useSupportConversation(visible: boolean) {
  const { client } = useApiClient();
  const { user } = useStytchUser();
  const queryClient = useQueryClient();

  const stytchUserId = user?.user_id ?? null;
  const sessionKey = sessionQueryKey(stytchUserId);

  // Bootstrap the Chatwoot session once and share it across the messages query
  // and the send mutation (feedback sends without opening the thread, so the
  // mutation must be able to establish the session on its own).
  const bootstrapSession = useCallback(async (): Promise<SupportSession> => {
    // NOTE: `/me/support/identity` is not in the live-generated OpenAPI schema
    // yet, so this typed-client call is the one expected `npm run typecheck`
    // failure until the paired dashboard-api PR deploys. Do NOT silence it with
    // a cast or raw fetch — that is exactly the phantom-endpoint smuggling this
    // effort removes.
    const { data, error, response } = await client.GET("/me/support/identity");
    if (error || !data) {
      throwApiError(
        error,
        "You need to be signed in to message Louise.",
        "useSupportConversation.identity",
        response?.status,
      );
    }

    // Standard `{ data: { ... } }` response envelope (dashboard-api #1663).
    const identity = data.data;
    const contact = await identifyChatwootContact(
      identity.identifier,
      identity.identifierHash,
    );
    const conversationId = await findChatwootConversation(
      contact.contactIdentifier,
    );
    return { ...contact, conversationId };
  }, [client]);

  const ensureSession = useCallback(
    () =>
      queryClient.fetchQuery<SupportSession>({
        queryKey: sessionKey,
        queryFn: bootstrapSession,
        staleTime: Infinity,
      }),
    [bootstrapSession, queryClient, sessionKey],
  );

  const session = useQuery<SupportSession>({
    queryKey: sessionKey,
    queryFn: bootstrapSession,
    enabled: visible,
    staleTime: Infinity,
    retry: (failureCount, error) =>
      failureCount < 1 && retryLouiseQuery(failureCount, error),
  });

  const conversationId = session.data?.conversationId ?? null;
  const contactIdentifier = session.data?.contactIdentifier ?? null;
  const pubsubToken = session.data?.pubsubToken ?? null;
  const messagesKey = messagesQueryKey(stytchUserId, conversationId);

  const messages = useQuery<SupportMessage[]>({
    queryKey: messagesKey,
    enabled: visible && Boolean(contactIdentifier && conversationId),
    retry: (failureCount, error) =>
      failureCount < 1 && retryLouiseQuery(failureCount, error),
    staleTime: 0,
    refetchOnMount: "always",
    // Realtime (Chatwoot RoomChannel below) handles the instant path; this poll
    // is only a backstop for dropped/never-connected sockets.
    refetchInterval: visible ? 30_000 : false,
    queryFn: () =>
      listChatwootMessages(contactIdentifier as string, conversationId!),
  });

  // Realtime primary path: while the thread is open and we hold a pubsub token,
  // subscribe to Chatwoot's RoomChannel and invalidate the messages query on any
  // relevant event so agent replies land instantly. Re-runs (and tears the old
  // socket down first) whenever the token, conversation, or visibility changes,
  // so switching contacts never double-subscribes or leaks a socket.
  useEffect(() => {
    if (!visible || !pubsubToken) return;
    const key = messagesQueryKey(stytchUserId, conversationId);
    return subscribeToChatwootRoom(pubsubToken, () => {
      void queryClient.invalidateQueries({ queryKey: key });
    });
  }, [visible, pubsubToken, stytchUserId, conversationId, queryClient]);

  const sendMessage = useMutation({
    mutationFn: async ({
      content,
      entryPoint,
      attachments,
    }: {
      content: string;
      entryPoint: EntryPoint;
      attachments: SupportAttachmentDraft[];
    }): Promise<{ conversationId: number; message: SupportMessage }> => {
      const active = await ensureSession();
      const conversationId =
        active.conversationId ??
        (await createChatwootConversation(active.contactIdentifier));
      if (active.conversationId === null) {
        queryClient.setQueryData<SupportSession>(sessionKey, {
          ...active,
          conversationId,
        });
      }
      const message = await sendChatwootMessage(
        active.contactIdentifier,
        conversationId,
        content,
        attachments,
        entryPoint,
      );
      return { conversationId, message };
    },
    onSuccess: ({ conversationId: sentConversationId, message }) => {
      const key = messagesQueryKey(stytchUserId, sentConversationId);
      queryClient.setQueryData<SupportMessage[]>(key, (current) => {
        const list = current ?? [];
        return list.some((existing) => existing.id === message.id)
          ? list
          : [...list, message];
      });
      // Reconcile the local echo with the server so team replies (and messages
      // sent through the feedback entry point) appear as soon as it opens.
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  // Present a single query-like surface to the modal: loading and error states
  // fold the identity/contact bootstrap in with the message fetch.
  const enabled = visible;
  const conversation = {
    data: {
      conversationId,
      messages: messages.data ?? [],
    } satisfies SupportConversation,
    isPending:
      enabled &&
      (session.isPending ||
        (Boolean(conversationId) &&
          messages.isPending &&
          !sendMessage.isPending)),
    isError: session.isError || messages.isError,
    isFetching: session.isFetching || messages.isFetching,
    error:
      (session.error instanceof ApiRequestError ? session.error : null) ??
      (messages.error instanceof Error ? messages.error : null),
    refetch: () => {
      void session.refetch();
      if (conversationId) void messages.refetch();
    },
  };

  return { conversation, sendMessage };
}
